import ViewportPopover from '../../../components/common/ViewportPopover';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../context/PermissionsContext';
import { canAccessRoute } from '../../../utils/routeAccess';
import { ColumnFilter, filterReportRows } from '../../../utils/reportTableFilters';
import { formatGiftDuration } from '../../../utils/giftDuration';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, Check, ChevronDown, ChevronLeft, ChevronRight, FileSpreadsheet, FileText, RefreshCw, Search, X } from 'lucide-react';
import {
  ADMINISTRATIVE_REPORT_TYPES,
  AdministrativeReportColumn,
  AdministrativeReportFilterOption,
  AdministrativeReportFilterOptions,
  AdministrativeReportFilters,
  AdministrativeReportGroupOption,
  AdministrativeReportResponse,
  AdministrativeReportType,
  fetchAdministrativeReport,
  fetchAdministrativeReportOptions,
} from '../../../services/administrativeReportsService.ts';
// This module is delivered by the report-export slice. Keeping exports behind these two actions
// ensures both formats receive the exact same visible dataset.
// @ts-ignore -- implemented by the administrative report export task
import { exportAdministrativeReportExcel, exportAdministrativeReportPdf } from '../../../utils/administrativeReportExports.ts';

const PAGE_SIZE = 10;
const EMPTY_REPORT: AdministrativeReportResponse = { columns: [], rows: [], summary: {}, group_options: [] };
const EMPTY_OPTIONS: AdministrativeReportFilterOptions = {
  clientes: [], sucursales: [], servicios: [], espacios: [], tipos_pago: [], usuarios: [],
};

const REPORTS: Record<AdministrativeReportType, { title: string; description: string; color: string }> = {
  'horas-regalo': { title: 'Horas de regalo', description: 'Consumo del titular y asociados.', color: '#61257B' },
  'deudas': { title: 'Deudas de clientes', description: 'Saldos pendientes del titular.', color: '#61257B' },
  'pagos-sesiones': { title: 'Pagos por sesiones POS', description: 'Apertura, reservas y operadores de cobro.', color: '#61257B' },
  cafeteria: { title: 'Ventas de cafetería', description: 'Ventas, cobros y saldos de cafetería.', color: '#C1348B' },
  cancelaciones: { title: 'Cancelaciones de reservas', description: 'Reservas canceladas, titular y motivo. Fechas según la agenda de la reserva.', color: '#61257B' },
  reservas: { title: 'Reservas y ocupación', description: 'Estados, agenda y ocupación por sede.', color: '#FF2052' },
  pagos: { title: 'Pagos', description: 'Ingresos, medios de pago y transacciones.', color: '#007FC8' },
  caja: { title: 'Caja', description: 'Movimientos, aperturas y cierres de caja.', color: '#EC8400' },
  uso: { title: 'Uso de espacios', description: 'Uso de servicios, espacios y horas.', color: '#00A98F' },
  clientes: { title: 'Clientes', description: 'Actividad, recurrencia y consumo de clientes.', color: '#9B004A' },
  actividad: { title: 'Actividad', description: 'Auditoría de acciones administrativas.', color: '#007FC8' },
  seguridad: { title: 'Seguridad', description: 'Accesos, resultados y eventos de seguridad.', color: '#EC8400' },
};

const reportTypeFromPath = (pathname: string): AdministrativeReportType => {
  const candidate = pathname.split('/').filter(Boolean).pop();
  return ADMINISTRATIVE_REPORT_TYPES.includes(candidate as AdministrativeReportType)
    ? candidate as AdministrativeReportType
    : 'reservas';
};

const groupRows = (
  rows: Record<string, unknown>[],
  columns: AdministrativeReportColumn[],
  groupKeys: string[],
  groupOptions: { key: string; label: string }[] = [],
) => {
  if (!groupKeys.length) return { rows, columns };
  const optionColumns = groupKeys.map(groupKey => columns.find(column => column.key === groupKey) || {
    key: groupKey,
    label: groupOptions.find(option => option.key === groupKey)?.label || groupKey,
    type: groupKey.includes('fecha') ? 'date' as const : 'text' as const,
  });
  const selected = new Set(groupKeys);
  const aggregateColumns = columns.filter(column => !selected.has(column.key) && column.aggregate === 'sum');
  const buckets = new Map<string, Record<string, unknown>>();

  rows.forEach(row => {
    const groupValues = groupKeys.map(groupKey => {
      const rawGroup = row[groupKey];
      return rawGroup === undefined || rawGroup === null || rawGroup === '' ? 'Sin especificar' : String(rawGroup);
    });
    const bucketKey = JSON.stringify(groupValues);
    const initial = Object.fromEntries(groupKeys.map((groupKey, index) => [groupKey, groupValues[index]]));
    const bucket = buckets.get(bucketKey) || { ...initial, registros: 0 };
    bucket.registros = Number(bucket.registros) + 1;
    aggregateColumns.forEach(column => {
      const value = Number(row[column.key]);
      if (!Number.isFinite(value)) return;
      if (column.type === 'money' || column.type === 'currency') {
        const centsKey = `__cents_${column.key}`;
        const cents = Number(bucket[centsKey] || 0) + Math.round(value * 100);
        bucket[centsKey] = cents;
        bucket[column.key] = (cents / 100).toFixed(2);
      } else {
        bucket[column.key] = Number(bucket[column.key] || 0) + value;
      }
    });
    buckets.set(bucketKey, bucket);
  });

  const groupedRows = Array.from(buckets.values()).map(bucket => {
    Object.keys(bucket).filter(key => key.startsWith('__cents_')).forEach(key => delete bucket[key]);
    return bucket;
  });
  return {
    rows: groupedRows,
    columns: [...optionColumns, { key: 'registros', label: 'Registros', type: 'number' as const }, ...aggregateColumns],
  };
};

const humanize = (value: string) => value.replace(/_/g, ' ').replace(/^./, letter => letter.toUpperCase());

const BOLIVIA_TIME_ZONE = 'America/La_Paz';

const boliviaDateStamp = (value = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BOLIVIA_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(value);
  const get = (type: string) => parts.find(part => part.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const formatCell = (value: unknown, column: AdministrativeReportColumn) => {
  if (value === null || value === undefined || value === '') return '—';
  if (column.key === 'registros') return `${value} ${Number(value) === 1 ? 'registro' : 'registros'}`;
  if (column.type === 'currency' || column.type === 'money') {
    const number = Number(value);
    return Number.isFinite(number)
      ? `Bs ${new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number)}`
      : String(value);
  }
  if (column.type === 'duration') return formatGiftDuration(Number(value) / 60);
  if (column.type === 'number') {
    const number = Number(value);
    return Number.isFinite(number) ? new Intl.NumberFormat('es-BO').format(number) : String(value);
  }
  if (column.type === 'boolean') return value ? 'Sí' : 'No';
  if (column.type === 'date') {
    const text = String(value);
    const date = /^\d{4}-\d{2}-\d{2}$/.test(text) ? new Date(`${text}T00:00:00`) : new Date(text);
    return Number.isNaN(date.getTime()) ? text : date.toLocaleDateString('es-BO', { dateStyle: 'medium' });
  }
  if (column.type === 'datetime') {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('es-BO', { dateStyle: 'medium', timeStyle: 'short', timeZone: BOLIVIA_TIME_ZONE });
  }
  return String(value);
};

const controlClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-[#007FC8] focus:ring-2 focus:ring-[#007FC8]/20';

const ReportsPage: React.FC = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { permissions, disabledPaths, loading: permissionsLoading } = usePermissions();
  const [tableSearch, setTableSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, ColumnFilter>>({});
  const [tableFiltersOpen, setTableFiltersOpen] = useState(false);
  const reportType = reportTypeFromPath(pathname);
  const meta = REPORTS[reportType];
  const [draftFilters, setDraftFilters] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [report, setReport] = useState<AdministrativeReportResponse>(EMPTY_REPORT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportError, setExportError] = useState('');
  const [filterOptions, setFilterOptions] = useState<AdministrativeReportFilterOptions>(EMPTY_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState('');
  const [groupKeys, setGroupKeys] = useState<string[]>([]);
  const [groupOpen, setGroupOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const previousType = useRef(reportType);
  const exportContainerRef = useRef<HTMLDivElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const firstExportRef = useRef<HTMLButtonElement>(null);
  const groupContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (previousType.current === reportType) return;
    previousType.current = reportType;
    setTableSearch('');
    setColumnFilters({});
    setTableFiltersOpen(false);
    setDraftFilters({});
    setFilters({});
    setGroupKeys([]);
    setGroupOpen(false);
    setPage(1);
  }, [reportType]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    firstExportRef.current?.focus();
    const close = (restoreFocus = false) => {
      setExportOpen(false);
      if (restoreFocus) exportButtonRef.current?.focus();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    const onMouseDown = (event: MouseEvent) => {
      if (!exportContainerRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [exportOpen]);

  useEffect(() => {
    let active = true;
    setOptionsLoading(true);
    setOptionsError('');
    fetchAdministrativeReportOptions()
      .then((data: AdministrativeReportFilterOptions) => {
        if (!active) return;
        setFilterOptions(data);
        setOptionsLoading(false);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setFilterOptions(EMPTY_OPTIONS);
        setOptionsError(reason instanceof Error ? reason.message : 'No fue posible cargar las opciones de filtros.');
        setOptionsLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setExportError('');
    setExportOpen(false);
    setReport(EMPTY_REPORT);
    fetchAdministrativeReport(reportType, filters as AdministrativeReportFilters)
      .then(data => {
        if (!active) return;
        setReport({
          columns: Array.isArray(data?.columns) ? data.columns : [],
          rows: Array.isArray(data?.rows) ? data.rows : [],
          summary: data?.summary && typeof data.summary === 'object' ? data.summary : {},
          group_options: Array.isArray(data?.group_options) ? data.group_options : [],
        });
        setLoading(false);
      })
      .catch(reason => {
        if (!active) return;
        setError(reason instanceof Error ? reason.message : 'No fue posible cargar el reporte.');
        setLoading(false);
      });
    return () => { active = false; };
  }, [reportType, filters]);

  const filteredRows = useMemo(() => filterReportRows(report.rows, report.columns, tableSearch, columnFilters), [report.rows, report.columns, tableSearch, columnFilters]);
  const hasTableFilters = Boolean(tableSearch.trim() || Object.values(columnFilters).some(filter => filter.text || filter.min || filter.max));
  const visibleSummary = hasTableFilters ? Object.fromEntries([
    ['registros', filteredRows.length],
    ...report.columns.filter(column => column.aggregate === 'sum').map(column => {
      const total = filteredRows.reduce((sum, row) => sum + (Number(row[column.key]) || 0), 0);
      return [column.label, formatCell(total, column)];
    }),
  ]) : report.summary;
  const grouped = useMemo(
    () => groupRows(filteredRows, report.columns, groupKeys, report.group_options),
    [report, filteredRows, groupKeys],
  );
  const pageCount = Math.max(1, Math.ceil(grouped.rows.length / PAGE_SIZE));
  const visibleRows = grouped.rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [groupKeys, filteredRows]);

  const updateFilter = (key: string, value: string) => setDraftFilters(current => ({ ...current, [key]: value }));
  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setFilters({ ...draftFilters });
  };

  const filterPresentation: Record<string, { label: string; options?: AdministrativeReportFilterOption[] }> = {
    fecha_desde: { label: 'Fecha desde' },
    fecha_hasta: { label: 'Fecha hasta' },
    sucursal_id: { label: 'Sucursal', options: filterOptions.sucursales },
    cliente_id: { label: 'Cliente', options: filterOptions.clientes },
    servicio_id: { label: 'Servicio', options: filterOptions.servicios },
    espacio_id: { label: 'Espacio', options: filterOptions.espacios },
    tipo_pago_id: { label: 'Tipo de pago', options: filterOptions.tipos_pago },
    usuario_id: { label: reportType === 'caja' ? 'Usuario de caja' : reportType === 'pagos-sesiones' ? 'Operador o apertura de caja' : 'Usuario', options: filterOptions.usuarios },
    codigo: { label: reportType === 'cafeteria' ? 'Venta' : 'Reserva, venta o pago' },
    estado: { label: reportType === 'cafeteria' ? 'Estado de pago' : 'Estado de reserva' },
    accion: { label: 'Acción' },
    ip: { label: 'Dirección IP' },
    resultado: { label: 'Resultado' },
  };
  const exportFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value).map(([key, value]) => {
      const presentation = filterPresentation[key];
      const selectedOption = presentation?.options?.find(option => String(option.id) === String(value));
      const selectedLabel = selectedOption
        ? `${selectedOption.label}${selectedOption.detail ? ` — ${selectedOption.detail}` : ''} (ID: ${selectedOption.id})`
        : value;
      return [presentation?.label || humanize(key), selectedLabel];
    }),
  );

  const exportInput = {
    title: `Reporte de ${meta.title.toLowerCase()}`,
    filename: `reporte_${reportType}_${boliviaDateStamp()}`,
    filters: {...exportFilters, ...(tableSearch ? {'Búsqueda en tabla': tableSearch} : {}),
      ...Object.fromEntries(Object.entries(columnFilters).filter(([,filter]) => filter.text || filter.min || filter.max).map(([key,filter]) => [
        report.columns.find(col => col.key === key)?.label || key,
        [filter.text, filter.min ? `Desde ${filter.min}` : '', filter.max ? `Hasta ${filter.max}` : ''].filter(Boolean).join(' · ')
      ]))},
    columns: grouped.columns.map(column => ({
      ...column,
      type: column.type === 'currency' ? 'money' as const : column.type === 'boolean' ? 'text' as const : column.type,
    })),
    rows: grouped.rows,
    summary: visibleSummary,
  };

  const runExport = async (format: 'pdf' | 'excel') => {
    if (loading || error || exporting || !grouped.rows.length) return;
    setExportError('');
    setExporting(true);
    try {
      if (format === 'pdf') {
        exportAdministrativeReportPdf(exportInput);
      } else {
        await exportAdministrativeReportExcel(exportInput);
      }
    } catch {
      setExportError('No fue posible generar el archivo.');
    } finally {
      setExporting(false);
      setExportOpen(false);
      exportButtonRef.current?.focus();
    }
  };

  const entityFilter = (
    label: string,
    key: string,
    options: AdministrativeReportFilterOption[],
  ) => <SearchableFilter
    label={label}
    value={draftFilters[key] || ''}
    options={options}
    loading={optionsLoading}
    onChange={value => updateFilter(key, value)}
  />;

  const filterField = (key: string) => {
    const info = filterPresentation[key];
    return info.options ? entityFilter(info.label, key, info.options)
      : <FilterInput label={info.label} value={draftFilters[key] || ''} onChange={value => updateFilter(key, value)} />;
  };

  const reportFilters = () => {
    if (reportType === 'horas-regalo') return <>
      {filterField('cliente_id')}{filterField('sucursal_id')}{filterField('servicio_id')}{filterField('usuario_id')}{filterField('codigo')}
    </>;
    if (reportType === 'pagos-sesiones') return <>
      {filterField('cliente_id')}{filterField('sucursal_id')}{filterField('tipo_pago_id')}{filterField('usuario_id')}{filterField('codigo')}
    </>;
    if (reportType === 'reservas' || reportType === 'cancelaciones' || reportType === 'deudas') return <>
      {entityFilter('Sucursal', 'sucursal_id', filterOptions.sucursales)}
      {entityFilter('Cliente', 'cliente_id', filterOptions.clientes)}
      {entityFilter('Servicio', 'servicio_id', filterOptions.servicios)}
      {entityFilter('Espacio', 'espacio_id', filterOptions.espacios)}
      <FilterSelect label="Estado de reserva" value={draftFilters.estado || ''} onChange={value => updateFilter('estado', value)} options={['Pendiente', 'Confirmada', 'Finalizada']} />
    </>;
    if (reportType === 'cafeteria') return <>
      {entityFilter('Sucursal', 'sucursal_id', filterOptions.sucursales)}
      {entityFilter('Cliente', 'cliente_id', filterOptions.clientes)}
      <FilterInput label="Venta" value={draftFilters.codigo || ''} onChange={value => updateFilter('codigo', value)} />
      <FilterSelect label="Estado de pago" value={draftFilters.estado || ''} onChange={value => updateFilter('estado', value)} options={['pendiente', 'parcial', 'pagado']} />
    </>;
    if (reportType === 'pagos') return <>
      {entityFilter('Sucursal', 'sucursal_id', filterOptions.sucursales)}
      {entityFilter('Cliente', 'cliente_id', filterOptions.clientes)}
      {entityFilter('Tipo de pago', 'tipo_pago_id', filterOptions.tipos_pago)}
      <FilterInput label="Reserva, venta o pago" value={draftFilters.codigo || ''} onChange={value => updateFilter('codigo', value)} />
    </>;
    if (reportType === 'caja') return <>
      {entityFilter('Sucursal', 'sucursal_id', filterOptions.sucursales)}
      {entityFilter('Usuario de caja', 'usuario_id', filterOptions.usuarios)}
    </>;
    if (reportType === 'uso' || reportType === 'clientes') return <>
      {entityFilter('Sucursal', 'sucursal_id', filterOptions.sucursales)}
      {entityFilter('Cliente', 'cliente_id', filterOptions.clientes)}
      {entityFilter('Servicio', 'servicio_id', filterOptions.servicios)}
      {entityFilter('Espacio', 'espacio_id', filterOptions.espacios)}
    </>;
    if (reportType === 'actividad') return <>
      {entityFilter('Usuario', 'usuario_id', filterOptions.usuarios)}
      <FilterInput label="Acción" value={draftFilters.accion || ''} onChange={value => updateFilter('accion', value)} />
    </>;
    return <>
      {entityFilter('Usuario', 'usuario_id', filterOptions.usuarios)}
      <FilterInput label="Acción" value={draftFilters.accion || ''} onChange={value => updateFilter('accion', value)} />
      <FilterInput label="Dirección IP" value={draftFilters.ip || ''} onChange={value => updateFilter('ip', value)} />
      <FilterInput label="Resultado" value={draftFilters.resultado || ''} onChange={value => updateFilter('resultado', value)} />
    </>;
  };

  return (
    <main className="min-h-full bg-white px-4 py-6 sm:px-6 lg:px-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="mx-auto max-w-[1500px]">
        <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em]" style={{ color: meta.color }}><BarChart3 size={18} /> Centro de reportes</div>
            <h1 className="text-3xl font-black tracking-tight text-slate-950">{meta.title}</h1>
            <p className="mt-1 text-sm text-slate-500">{meta.description}</p>
          </div>
          <div className="relative" ref={exportContainerRef}>
            <button ref={exportButtonRef} type="button" onClick={() => setExportOpen(open => !open)} disabled={loading || !!error || exporting || !grouped.rows.length} aria-haspopup="true" aria-controls="report-export-options" aria-expanded={exportOpen} className="inline-flex items-center gap-2 rounded-xl bg-[#FF2052] px-4 py-2 text-sm font-bold text-white disabled:opacity-40"><FileText size={17} /> {exporting ? 'Generando…' : 'Generar reporte'} <ChevronDown size={16} /></button>
            {exportOpen && <div id="report-export-options" role="group" aria-label="Formatos de exportación" className="absolute right-0 z-20 mt-2 min-w-48 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
              <button ref={firstExportRef} disabled={exporting} type="button" onClick={() => void runExport('pdf')} aria-label="Descargar PDF" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40"><FileText size={17} className="text-[#FF2052]" /> Formato PDF</button>
              <button disabled={exporting} type="button" onClick={() => void runExport('excel')} aria-label="Descargar Excel" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40"><FileSpreadsheet size={17} className="text-[#00A98F]" /> Formato Excel</button>
            </div>}
          </div>
        </header>

        <nav aria-label="Tipos de reporte" className="mb-6 flex flex-wrap items-start gap-2">
          {ADMINISTRATIVE_REPORT_TYPES.filter(type => !permissionsLoading && canAccessRoute(`/reportes/${type}`, user?.role, permissions, disabledPaths)).map(type => (
            <Link key={type} to={`/reportes/${type}`} aria-current={type === reportType ? 'page' : undefined} className={`inline-flex items-center whitespace-nowrap rounded-xl border px-3 py-2 text-sm font-bold no-underline transition ${type === reportType ? 'border-transparent text-white shadow-md' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`} style={type === reportType ? { background: REPORTS[type].color } : undefined}>{REPORTS[type].title}</Link>
          ))}
        </nav>

        <form onSubmit={applyFilters} className="mb-6 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            <FilterInput label="Fecha desde" type="date" value={draftFilters.fecha_desde || ''} onChange={value => updateFilter('fecha_desde', value)} />
            <FilterInput label="Fecha hasta" type="date" value={draftFilters.fecha_hasta || ''} onChange={value => updateFilter('fecha_hasta', value)} />
            {reportFilters()}
            <div className="flex items-end"><button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#007FC8] px-4 py-2.5 text-sm font-bold text-white"><Search size={17} /> Aplicar filtros</button></div>
          </div>
        </form>

        {optionsError && <div role="alert" className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">No se pudieron cargar los dropdowns: {optionsError}</div>}
        {exportError && <div role="alert" className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{exportError}</div>}
        {loading ? (
          <StatePanel role="status"><RefreshCw className="animate-spin text-[#007FC8]" /> <span>Cargando reporte…</span></StatePanel>
        ) : error ? (
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center font-semibold text-red-700">{error}</div>
        ) : (
          <>
            {!!Object.keys(visibleSummary).length && <section aria-label="Indicadores del reporte" className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Object.entries(visibleSummary).map(([key, value], index) => <div key={key} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" style={{ borderTop: `4px solid ${['#FF2052', '#007FC8', '#EC8400', '#00A98F'][index % 4]}` }}><div className="text-xs font-bold uppercase tracking-wide text-slate-400">{humanize(key)}</div><div className="mt-1 text-2xl font-black text-slate-900">{String(value ?? '—')}</div></div>)}
            </section>}

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-start sm:justify-between">
                <div><h2 className="font-black text-slate-900">Detalle del reporte</h2><p className="text-xs text-slate-500">{grouped.rows.length} resultado{grouped.rows.length === 1 ? '' : 's'}</p></div>
                <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:max-w-2xl sm:justify-end">
                  {groupKeys.map(key => {
                    const option = report.group_options.find((item: AdministrativeReportGroupOption) => item.key === key);
                    return <span key={key} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-[#007FC8]">{option?.label || humanize(key)}<button type="button" aria-label={`Quitar agrupación ${option?.label || humanize(key)}`} onClick={() => setGroupKeys(keys => keys.filter(item => item !== key))} className="rounded-full p-0.5 hover:bg-blue-100"><X size={13} /></button></span>;
                  })}
                  <div className="relative w-full sm:w-auto" ref={groupContainerRef}>
                    <button type="button" aria-haspopup="true" aria-controls="grouping-options" aria-expanded={groupOpen} onClick={() => setGroupOpen(open => !open)} className={`${controlClass} inline-flex items-center justify-between gap-2 font-bold text-slate-700`}>
                      Configurar agrupación <ChevronDown size={16} />
                    </button>
                    {groupOpen && <ViewportPopover anchor={() => groupContainerRef.current} onClose={() => setGroupOpen(false)} id="grouping-options" role="group" aria-label="Dimensiones de agrupación" className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                      <p className="px-2 pb-2 text-xs font-semibold text-slate-400">Selecciona una o más dimensiones</p>
                      {report.group_options.map((option: AdministrativeReportGroupOption) => <label key={option.key} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        <input type="checkbox" aria-label={option.label} checked={groupKeys.includes(option.key)} onChange={() => setGroupKeys(keys => keys.includes(option.key) ? keys.filter(key => key !== option.key) : [...keys, option.key])} className="h-4 w-4 rounded border-slate-300 text-[#007FC8]" />
                        {option.label}
                      </label>)}
                      {groupKeys.length > 0 && <button type="button" onClick={() => setGroupKeys([])} className="mt-1 w-full rounded-lg px-2 py-2 text-left text-xs font-bold text-[#FF2052] hover:bg-red-50">Quitar todas</button>}
                    </ViewportPopover>}
                  </div>
                </div>
              </div>
              <div className="space-y-3 border-b border-slate-100 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1"><FilterInput label="Buscar en la tabla" value={tableSearch} onChange={setTableSearch} /></div>
                  <button type="button" aria-expanded={tableFiltersOpen} onClick={() => setTableFiltersOpen(value => !value)} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700">Filtros por columna</button>
                  {hasTableFilters && <button type="button" onClick={() => {setTableSearch(''); setColumnFilters({});}} className="rounded-xl px-4 py-2 text-sm font-bold text-[#FF2052]">Limpiar filtros de tabla</button>}
                </div>
                <p className="text-xs text-slate-500">{filteredRows.length} de {report.rows.length} registros · Los filtros se aplican antes de agrupar y se incluyen en las exportaciones.</p>
                {tableFiltersOpen && <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {report.columns.map(column => {
                    const filter = columnFilters[column.key] || {};
                    const numeric = ['number','money','currency','duration'].includes(column.type || '');
                    const range = numeric || column.type === 'date' || column.type === 'datetime';
                    const label = `${column.label}${column.type === 'duration' ? ' (minutos)' : ''}`;
                    const update = (key: keyof ColumnFilter, value: string) => setColumnFilters(current => ({...current, [column.key]: {...current[column.key], [key]: value}}));
                    return <div key={column.key} className="min-w-0 rounded-xl bg-slate-50 p-3">
                      {range ? <><p className="mb-2 text-xs font-bold text-slate-600">{label}</p><div className="grid min-w-0 grid-cols-2 gap-2">
                        <FilterInput label={`${label} desde`} type={numeric ? 'number' : 'date'} value={filter.min || ''} onChange={value => update('min', value)} />
                        <FilterInput label={`${label} hasta`} type={numeric ? 'number' : 'date'} value={filter.max || ''} onChange={value => update('max', value)} />
                      </div></> : <FilterInput label={label} value={filter.text || ''} onChange={value => update('text', value)} />}
                    </div>;
                  })}
                </div>}
              </div>
              {!grouped.rows.length ? <StatePanel>No hay resultados para los filtros seleccionados.</StatePanel> : <>
                <div className="overflow-x-auto"><table className="min-w-full border-collapse text-left text-sm"><thead className="bg-slate-50"><tr>{grouped.columns.map(column => <th key={column.key} scope="col" className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-500">{column.label}</th>)}</tr></thead><tbody>{visibleRows.map((row, rowIndex) => <tr key={`${page}-${rowIndex}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70">{grouped.columns.map(column => <td key={column.key} className="whitespace-nowrap px-4 py-3 font-medium text-slate-700">{formatCell(row[column.key], column)}</td>)}</tr>)}</tbody></table></div>
                <footer className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500"><span>Página {page} de {pageCount}</span><div className="flex gap-2"><button type="button" aria-label="Página anterior" disabled={page === 1} onClick={() => setPage(value => Math.max(1, value - 1))} className="rounded-lg border border-slate-200 p-2 disabled:opacity-30"><ChevronLeft size={16} /></button><button type="button" aria-label="Página siguiente" disabled={page === pageCount} onClick={() => setPage(value => Math.min(pageCount, value + 1))} className="rounded-lg border border-slate-200 p-2 disabled:opacity-30"><ChevronRight size={16} /></button></div></footer>
              </>}
            </section>
          </>
        )}
      </div>
    </main>
  );
};

const StatePanel: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className = '', ...props }) => <div {...props} className={`flex min-h-48 items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-8 text-center font-semibold text-slate-500 ${className}`}>{children}</div>;

const normalizeSearch = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();

const SearchableFilter: React.FC<{
  label: string;
  value: string;
  options: AdministrativeReportFilterOption[];
  loading?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, options, loading = false, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = `report-filter-${label.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const selected = options.find(option => String(option.id) === value);
  const queryTokens = normalizeSearch(query).split(/\s+/).filter(Boolean);
  const visibleOptions = options.filter(option => {
    const haystack = normalizeSearch(`${option.label} ${option.detail}`);
    return queryTokens.every(token => haystack.includes(token));
  });
  const close = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  return <div ref={rootRef} className="relative block text-xs font-bold uppercase tracking-wide text-slate-500">
    <label htmlFor={`${listId}-input`}>{label}</label>
    <div className="relative mt-1">
      <input
        id={`${listId}-input`}
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        aria-activedescendant={open && activeIndex >= 0 ? `${listId}-option-${visibleOptions[activeIndex]?.id}` : undefined}
        autoComplete="off"
        disabled={loading}
        value={open ? query : selected?.label || ''}
        placeholder={loading ? 'Cargando…' : 'Buscar o seleccionar'}
        onFocus={() => { setQuery(''); setActiveIndex(-1); setOpen(true); }}
        onChange={event => {
          if (value) onChange('');
          setQuery(event.target.value);
          setActiveIndex(-1);
          setOpen(true);
        }}
        onBlur={event => {
          if (!rootRef.current?.contains(event.relatedTarget as Node | null) && !document.getElementById(listId)?.contains(event.relatedTarget as Node | null)) close();
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' && visibleOptions.length) {
            event.preventDefault();
            setOpen(true);
            setActiveIndex(index => Math.min(index + 1, visibleOptions.length - 1));
          } else if (event.key === 'ArrowUp' && visibleOptions.length) {
            event.preventDefault();
            setOpen(true);
            setActiveIndex(index => index < 0 ? visibleOptions.length - 1 : Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && open && activeIndex >= 0 && visibleOptions[activeIndex]) {
            event.preventDefault();
            onChange(String(visibleOptions[activeIndex].id));
            close();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
          } else if (event.key === 'Tab') {
            close();
          }
        }}
        className={`${controlClass} pr-16 normal-case disabled:bg-slate-100`}
      />
      {selected && !open && <button type="button" aria-label={`Limpiar ${label}`} onClick={() => onChange('')} className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100"><X size={15} /></button>}
      <ChevronDown aria-hidden="true" size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
    {open && <ViewportPopover anchor={() => rootRef.current?.querySelector('input') || null} onClose={close} id={listId} role="listbox" aria-label={`Opciones de ${label}`} className="rounded-xl border border-slate-200 bg-white p-1 shadow-xl normal-case">
      <button type="button" tabIndex={-1} role="option" aria-selected={!value} onMouseDown={event => event.preventDefault()} onClick={() => { onChange(''); close(); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50">
        <span className="flex-1">Todos</span>{!value && <Check size={15} />}
      </button>
      {visibleOptions.map((option, index) => <button id={`${listId}-option-${option.id}`} key={option.id} type="button" tabIndex={-1} role="option" aria-selected={String(option.id) === value} onMouseEnter={() => setActiveIndex(index)} onMouseDown={event => event.preventDefault()} onClick={() => { onChange(String(option.id)); close(); }} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 ${activeIndex === index ? 'bg-slate-50' : ''}`}>
        <span className="min-w-0 flex-1"><span className="block truncate font-bold">{option.label}</span>{option.detail && <span className="block truncate text-xs font-medium text-slate-400">{option.detail}</span>}</span>
        {String(option.id) === value && <Check size={15} className="shrink-0 text-[#007FC8]" />}
      </button>)}
      {!visibleOptions.length && <div className="px-3 py-4 text-center text-sm font-medium text-slate-400">Sin coincidencias</div>}
    </ViewportPopover>}
  </div>;
};

const FilterInput: React.FC<{ label: string; value: string; onChange: (value: string) => void; type?: string; placeholder?: string }> = ({ label, value, onChange, type = 'text', placeholder }) => <label className="block min-w-0 text-xs font-bold uppercase tracking-wide text-slate-500">{label}<input aria-label={label} type={type} value={value} placeholder={placeholder} onChange={event => onChange(event.target.value)} className={`${controlClass} mt-1`} /></label>;

const FilterSelect: React.FC<{ label: string; value: string; onChange: (value: string) => void; options: string[] }> = ({ label, value, onChange, options }) => <label className="block min-w-0 text-xs font-bold uppercase tracking-wide text-slate-500">{label}<select aria-label={label} value={value} onChange={event => onChange(event.target.value)} className={`${controlClass} mt-1`}><option value="">Todos</option>{options.map(option => <option key={option} value={option}>{humanize(option)}</option>)}</select></label>;

export { boliviaDateStamp, formatCell, groupRows, reportTypeFromPath };
export default ReportsPage;
